import React, { Suspense, useMemo, useRef, useCallback, useEffect, useState } from 'react'
import { Form, Result, Skeleton, Space } from 'antd'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { FilterOutlined, SettingOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import B2BLayout from '../../layouts/b2b'
import FiltersList from '../../components/v2/filters'
import FiltersDrawer, { FiltersDrawerRef } from '../../components/v2/filters-drawer'
import { FiltersFastButtons } from '../../components/v2/filters-fast-buttons'
import DataTable from '../../components/data-table'
import StickyToolbar from '../../layouts/b2b/sticky-toolbar'
import { useHeaderActions } from '../../contexts/header-actions-context'
import { HeaderAction } from '../../contexts/header-actions-context/interface'
import { useMediaQuery } from 'react-responsive'
import { ModalCreateTaskByHrResponse } from '../../views/modal-create-task-searchByHrResponse'
import { PrintButton } from './ui/print-button/print-button'
import { TableSettingsButton } from '../../components/v2/table-settings-button'
import { CONSTANTS } from './constants'
import { createSession } from './selectors'
import { getMobileFields } from './mobile-fields'
import { getFilters } from './filters'
import { TableWrapper } from './styles'
import {
  useTasksData,
  useTasksEnums,
  useTasksColumns,
  useTasksTable,
  useTasksMaps,
  useTableSettings,
} from './hooks'
import { hasPermission } from 'src/react-app/lib/permissions'
import { Permissions } from 'src/react-app/constants/permissions'

export default function AdminObjectsListPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { sessionData } = useSelector(createSession)
  const { setHeaderActions } = useHeaderActions()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [filtersForm] = Form.useForm()

  const headerRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const tableSettingsRef = useRef<{ open: () => void } | null>(null)

  const isLeader = sessionData?.organization?.id === CONSTANTS.LEADER_ORG_ID
  const isClaimLeader = useMemo(
    () => hasPermission(sessionData?.permissionsWithScope, Permissions.ACCESS_CLAIMS),
    [sessionData?.permissionsWithScope],
  )
  const isClaimOffice = sessionData?.user?.office?.id === CONSTANTS.CLAIM_OFFICE_ID
  const taskType =
    isClaimOffice || isClaimLeader
      ? 'claim'
      : sessionData?.organization?.type === CONSTANTS.ORGANIZATION_TYPE_HR
      ? 'hr'
      : 'case'

  const {
    data,
    pageLoading: dataPageLoading,
    dataLoading,
    pageError,
    pagination,
    sorter,
    options,
    remove,
  } = useTasksData()

  const {
    enums,
    loading: enumsLoading,
    error: enumsError,
    montageTypes,
    measurementTypes,
    deliveryTypes,
    saleTypes,
    claimTypes,
  } = useTasksEnums({
    taskType,
    isLeader,
  })

  const pageLoading = dataPageLoading || enumsLoading

  const criticalError =
    pageError ||
    (enumsError
      ? {
          code: 'error' as const,
          message: enumsError,
        }
      : null)

  const breadcrumbs = useMemo(
    () => [
      {
        href: location.pathname,
        title: 'Задачи',
      },
    ],
    [location.pathname],
  )

  const { stagesMap, taskTypesMap } = useTasksMaps(enums)

  const addDataColumns = useTasksColumns({
    sorter,
    stagesMap,
    taskTypesMap,
    isLeader,
    onRemove: remove,
  })

  const { viewColumns, settingsColumns } = useTableSettings(addDataColumns)

  const { handlerTableChange, cbTableRowClick, rowClassName, excelPath } = useTasksTable()

  const mobileFields = useMemo(() => getMobileFields(enums), [enums])

  const [currentFilters, setCurrentFilters] = useState<any>({})

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const filters: any = {}

    const id = params.get('id')
    if (id) filters.id = id

    const stage = params.get('stage')
    if (stage) filters.stage = stage

    const taskTypeParam = params.get('task_type')
    if (taskTypeParam) filters.task_type = taskTypeParam

    const responsible = params.get('responsible')
    if (responsible && !isNaN(+responsible)) filters.responsible = +responsible

    const dates = params.get('dates')
    if (dates) filters.dates = dates

    setCurrentFilters(filters)
  }, [location.search])

  const filterFields = useMemo(() => {
    return getFilters(currentFilters, enums)
  }, [currentFilters, enums])

  const changeFiltersHandler = useCallback(
    filters => {
      const tmpFilter = { ...(filters || {}), pageSize: pagination.pageSize }

      if (Array.isArray(tmpFilter.stage)) {
        tmpFilter.stage = tmpFilter.stage.length > 0 ? tmpFilter.stage.join(',') : undefined
      }
      if (Array.isArray(tmpFilter.task_type)) {
        tmpFilter.task_type =
          tmpFilter.task_type.length > 0 ? tmpFilter.task_type.join(',') : undefined
      }

      Object.keys(tmpFilter).forEach(key => {
        if (tmpFilter[key] === '' || tmpFilter[key] === undefined || tmpFilter[key] === null) {
          delete tmpFilter[key]
        }
      })

      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      navigate({ search: newUrlParams })
    },
    [pagination, navigate],
  )

  const fastButtons = useMemo(() => {
    const buttons: any[] = []

    if (sessionData?.user?.id) {
      buttons.push({
        name: 'Мои задачи',
        code: 'responsible',
        value: String(sessionData.user.id),
      })
    }

    const today = dayjs().format('YYYY.MM.DD')
    buttons.push({
      name: 'Задачи на сегодня',
      code: 'dates',
      value: `${today}-${today}`,
    })

    if (taskType === 'case') {
      if (saleTypes.length > 0) {
        buttons.push({
          name: 'Продажи',
          code: 'task_type',
          value: saleTypes.join(','),
        })
      }
      if (measurementTypes.length > 0) {
        buttons.push({
          name: 'Замеры',
          code: 'task_type',
          value: measurementTypes.join(','),
        })
      }
      if (deliveryTypes.length > 0) {
        buttons.push({
          name: 'Доставка',
          code: 'task_type',
          value: deliveryTypes.join(','),
        })
      }
      if (montageTypes.length > 0) {
        buttons.push({
          name: 'Монтажи',
          code: 'task_type',
          value: montageTypes.join(','),
        })
      }
    }

    if (claimTypes.length > 0) {
      buttons.push({
        name: 'Рекламации',
        code: 'task_type',
        value: claimTypes.join(','),
      })
    }

    const stageButtons = (enums?.stages || []).map((s: any) => ({
      name: s.label,
      code: 'stage',
      value: s.value,
    }))

    buttons.push(...stageButtons)

    return buttons
  }, [
    sessionData?.user?.id,
    taskType,
    saleTypes,
    measurementTypes,
    deliveryTypes,
    montageTypes,
    claimTypes,
    enums,
  ])

  useEffect(() => {
    const actions: HeaderAction[] = []

    if (filtersDrawerRef) {
      actions.push({
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => {
          filtersDrawerRef.current?.toggle()
        },
        tooltip: 'Фильтры',
      })
    }

    if (isMobile && settingsColumns) {
      actions.push({
        key: 'settings',
        icon: <SettingOutlined />,
        onClick: () => {
          tableSettingsRef.current?.open()
        },
        tooltip: 'Настройка колонок',
      })
    }

    setHeaderActions(actions)

    return () => {
      setHeaderActions([])
    }
  }, [setHeaderActions, settingsColumns, isMobile])

  const handlePageChange = (page: number, pageSize: number) => {
    const currentUrlParams = new URLSearchParams(location.search)
    currentUrlParams.set('page', page.toString())
    if (pageSize) {
      currentUrlParams.set('pageSize', pageSize.toString())
    }
    navigate({ search: currentUrlParams.toString() })
  }

  const handleRowClick = useCallback(
    record => {
      cbTableRowClick(record)
    },
    [cbTableRowClick],
  )

  const dataWithActions = useMemo(() => {
    return data.map(item => ({
      ...item,
      deleteRecord: remove,
    }))
  }, [data, remove])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle="Задачи" isViewScrollButton>
      {pageLoading && <Skeleton active />}

      {!pageLoading && criticalError && (
        <Result status={criticalError.code} title="Ошибка" subTitle={criticalError.message} />
      )}

      {!pageLoading && !criticalError && (
        <>
          <StickyToolbar
            ref={headerRef}
            collapsible={
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space align={'start'} wrap>
                  <Suspense fallback="">
                    <ModalCreateTaskByHrResponse
                      entityType={taskType}
                      titleInputSearch={taskType === 'hr' ? 'ID отклика' : 'Номер заказа'}
                      externalEnums={enums}
                    />
                  </Suspense>
                  <FiltersList
                    filters={filterFields.filter(f => ['id'].includes(f.name))}
                    onChange={changeFiltersHandler}
                    isChangeByButtons
                    form={filtersForm}
                  />
                </Space>
                <FiltersDrawer
                  filters={filterFields}
                  onChange={changeFiltersHandler}
                  currentFilters={currentFilters}
                  ref={filtersDrawerRef}
                />
              </Space>
            }
            persistent={
              isMobile ? (
                <>
                  {options?.length > 0 && options.includes('tasks_quickfilters') && (
                    <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
                  )}
                </>
              ) : (
                <Space
                  style={{ width: '100%', justifyContent: 'space-between' }}
                  align={'center'}
                  wrap
                >
                  {options?.length > 0 && options.includes('tasks_quickfilters') && (
                    <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
                  )}
                  <Space align={'center'} wrap>
                    <PrintButton path={excelPath} columns={viewColumns} stages={enums?.stages} />
                    <TableSettingsButton items={settingsColumns} ref={tableSettingsRef} />
                  </Space>
                </Space>
              )
            }
          />
          <TableWrapper>
            <DataTable
              columns={viewColumns}
              mobileFields={mobileFields}
              data={dataWithActions}
              loading={dataLoading}
              rowKey={r => String(r.id)}
              rowClassName={rowClassName}
              highlightLastRowPrefix={'task'}
              pagination={{
                current: pagination.current || 1,
                pageSize: pagination.pageSize || 10,
                total: pagination.total || 0,
              }}
              onRowClick={handleRowClick}
              onPageChange={handlePageChange}
              onTableChange={handlerTableChange}
              className={'tasks-table'}
              headerRef={headerRef}
              maxLines={1}
              bordered
              scroll={{ x: 'max-content' }}
            />
          </TableWrapper>
        </>
      )}
    </B2BLayout>
  )
}
